/**
 * Selection Modal Component
 *
 * A modal for selecting drivers or constructors in the team calculator.
 * Shows a scrollable list with team colors, names, prices, and points.
 * Respects budget constraints by disabling items that exceed remaining budget.
 */

import { Check, ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { m } from "@/paraglide/messages";

export interface SelectionItem {
  id: string;
  name: string;
  price: number;
  priceChange: number;
  racePoints: number;
  teamColor: string;
  teamName?: string;
}

interface SelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: SelectionItem[];
  onSelect: (id: string | null) => void;
  currentSelectedId: string | null;
  /** Map of item IDs to their current slot labels (e.g., "Slot 2") for items selected elsewhere */
  selectedElsewhereMap: Map<string, string>;
  remainingBudget: number;
  /** Whether showing predicted data (amber labels) or actual data */
  isPredictionMode: boolean;
}

export function SelectionModal({
  isOpen,
  onClose,
  title,
  items,
  onSelect,
  currentSelectedId,
  selectedElsewhereMap,
  remainingBudget,
  isPredictionMode,
}: SelectionModalProps) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"price" | "priceChange" | "racePoints">(
    "racePoints",
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const scrollPositionRef = useRef({ x: 0, y: 0 });

  const handleSortClick = useCallback(
    (col: "price" | "priceChange" | "racePoints") => {
      if (sortBy === col) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      } else {
        setSortBy(col);
        setSortDir("desc");
      }
    },
    [sortBy],
  );

  // Filter + sort items
  const filteredItems = useMemo(() => {
    const filtered = items.filter(
      (item) =>
        search === "" ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.teamName?.toLowerCase().includes(search.toLowerCase()),
    );
    const dir = sortDir === "desc" ? -1 : 1;
    return [...filtered].sort((a, b) => (a[sortBy] - b[sortBy]) * dir);
  }, [items, search, sortBy, sortDir]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open and preserve scroll position
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position BEFORE any DOM changes
      const savedScrollX = window.scrollX;
      const savedScrollY = window.scrollY;
      scrollPositionRef.current = { x: savedScrollX, y: savedScrollY };

      // Lock body scroll - use overflow hidden on html/body
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const originalWidth = document.body.style.width;

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.width = "100%";

      return () => {
        // Restore body styles first
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = originalWidth;

        // Restore scroll position immediately and synchronously
        window.scrollTo(savedScrollX, savedScrollY);
      };
    }
  }, [isOpen]);

  // Reset search when modal closes (render-time state adjustment)
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setSearch("");
    }
  }

  const handleSelect = useCallback(
    (id: string) => {
      onSelect(id);
      onClose();
    },
    [onSelect, onClose],
  );

  const handleClear = useCallback(() => {
    onSelect(null);
    onClose();
  }, [onSelect, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50" role="presentation">
      {/* Backdrop - clickable to close */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-default"
        onClick={onClose}
        aria-label={m.f1_modal_close()}
      />

      {/* Modal - fullscreen on mobile, centered on desktop */}
      <div
        className="absolute inset-0 h-full sm:inset-auto sm:h-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 w-full sm:max-w-md sm:max-h-[80vh] flex flex-col rounded-none sm:rounded-xl border-0 sm:border border-border bg-card shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2
            id="modal-title"
            className="font-display text-lg font-semibold text-foreground"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={m.f1_modal_search_placeholder()}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </div>
        </div>

        {/* Clear selection button (if something is selected) */}
        {currentSelectedId && (
          <div className="px-4 pt-3">
            <button
              type="button"
              onClick={handleClear}
              className="w-full py-2 px-3 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-sm font-medium hover:bg-destructive/20 transition-colors"
            >
              {m.f1_modal_clear_selection()}
            </button>
          </div>
        )}

        {/* Column headers + legend — same p-4 + flex structure as rows */}
        {filteredItems.length > 0 && (
          <div className="p-4 pb-1 border-b border-border/50">
            <div className="flex items-center gap-3">
              <div className="w-1 shrink-0" />
              <div className="flex-1 min-w-0" />
              <div className="flex items-center shrink-0 font-mono text-xs text-muted-foreground">
                {(["price", "priceChange", "racePoints"] as const).map(
                  (col) => {
                    const label =
                      col === "price"
                        ? "$"
                        : col === "priceChange"
                          ? isPredictionMode
                            ? "x\u0394$"
                            : "\u0394$"
                          : isPredictionMode
                            ? m.f1_modal_col_xpts()
                            : m.f1_modal_col_pts();
                    const width =
                      col === "price"
                        ? "w-[4.5rem]"
                        : col === "priceChange"
                          ? "w-10"
                          : "w-12";
                    const isActive = sortBy === col;
                    const SortIcon =
                      isActive && sortDir === "asc" ? ChevronUp : ChevronDown;
                    return (
                      <button
                        key={col}
                        type="button"
                        onClick={() => handleSortClick(col)}
                        className={`${width} flex items-center justify-center gap-0.5 hover:text-foreground transition-colors ${isActive ? "text-foreground" : ""}`}
                      >
                        {label}
                        <SortIcon
                          className={`h-3 w-3 shrink-0 ${isActive ? "opacity-100" : "opacity-30"}`}
                        />
                      </button>
                    );
                  },
                )}
              </div>
            </div>
            {isPredictionMode && (
              <p className="text-[10px] text-muted-foreground text-right mt-0.5">
                <span className="text-amber-500">
                  {m.f1_modal_legend_amber()}
                </span>{" "}
                {m.f1_modal_legend_predicted()}
                {" \u00B7 "}
                <span className="text-success">{m.f1_apex_legend_green()}</span>
                /
                <span className="text-destructive">
                  {m.f1_apex_legend_red()}
                </span>{" "}
                {m.f1_modal_legend_price_change()}
              </p>
            )}
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {search ? m.f1_modal_no_results() : m.f1_modal_no_items()}
            </div>
          ) : (
            filteredItems.map((item) => {
              const isCurrentlySelected = item.id === currentSelectedId;
              const selectedElsewhereSlot = selectedElsewhereMap.get(item.id);
              const isSelectedElsewhere = !!selectedElsewhereSlot;
              const exceedsBudget =
                !isCurrentlySelected &&
                !isSelectedElsewhere &&
                item.price > remainingBudget;
              // Only budget exceeds is truly disabled - items in other slots can be clicked to move them
              const isDisabled = exceedsBudget;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => !isDisabled && handleSelect(item.id)}
                  disabled={isDisabled}
                  className={`w-full p-4 flex items-center gap-3 border-b border-border/50 last:border-0 transition-colors text-left ${
                    isCurrentlySelected
                      ? "bg-success/10"
                      : isSelectedElsewhere
                        ? "bg-muted/30 opacity-60 hover:opacity-80 hover:bg-muted/50"
                        : isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-muted/50"
                  }`}
                >
                  {/* Team color bar */}
                  <div
                    className="w-1 h-8 rounded-full shrink-0"
                    style={{ backgroundColor: item.teamColor }}
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {item.name}
                    </div>
                    {item.teamName && (
                      <div className="text-xs text-muted-foreground truncate">
                        {item.teamName}
                      </div>
                    )}
                    {/* Show which slot this item is in */}
                    {isSelectedElsewhere && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {m.f1_modal_in_slot({
                          slot: selectedElsewhereSlot ?? "",
                        })}
                      </div>
                    )}
                  </div>

                  {/* Price & Points — single-row 3-column */}
                  <div className="flex items-center shrink-0 font-mono text-xs">
                    <span
                      className={`w-[4.5rem] text-right ${exceedsBudget ? "text-destructive" : "text-foreground"} text-sm`}
                    >
                      ${item.price.toFixed(1)}M
                    </span>
                    <span
                      className={`w-10 text-right ${
                        Math.abs(item.priceChange) < 0.05
                          ? "invisible"
                          : item.priceChange > 0
                            ? "text-success"
                            : "text-destructive"
                      }`}
                    >
                      {item.priceChange > 0 ? "+" : ""}
                      {item.priceChange.toFixed(1)}
                    </span>
                    <span
                      className={`w-12 text-right ${isPredictionMode ? "text-amber-500" : "text-foreground"}`}
                    >
                      {item.racePoints >= 0 ? "+" : ""}
                      {item.racePoints.toFixed(1)}
                    </span>
                  </div>

                  {/* Selected indicator */}
                  {isCurrentlySelected && (
                    <div className="p-1.5 rounded-full bg-success/20 shrink-0">
                      <Check className="h-3.5 w-3.5 text-success" />
                    </div>
                  )}
                  {/* Selected elsewhere indicator */}
                  {isSelectedElsewhere && (
                    <div className="p-1.5 rounded-full bg-muted shrink-0">
                      <Check className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
